import { hexToBytes, sanitizeHex } from "../commandUtils.js";

export function buildCommandTheme(dark, c, A) {
  const soft = (a) => `${A}${a}`;
  return {
    input: {
      width: "100%",
      height: 34,
      borderRadius: 8,
      border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : c.cardBdr}`,
      background: dark ? "rgba(255,255,255,0.04)" : "#fff",
      color: c.text,
      padding: "0 11px",
      fontSize: 13,
      fontFamily: "inherit",
      outline: "none",
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
      boxSizing: "border-box",
    },
    hexInput: {
      fontFamily: "var(--nm-font-mono)",
      fontSize: 12,
      letterSpacing: "0.03em",
    },
    focus: {
      onFocus: (e) => {
        e.currentTarget.style.borderColor = A;
        e.currentTarget.style.boxShadow = `0 0 0 3px ${soft("22")}`;
      },
      onBlur: (e) => {
        e.currentTarget.style.borderColor = dark ? "rgba(255,255,255,0.1)" : c.cardBdr;
        e.currentTarget.style.boxShadow = "none";
      },
    },
    section: {
      borderRadius: 12,
      border: `1px solid ${c.cardBdr}`,
      background: dark ? "rgba(255,255,255,0.02)" : c.card,
      overflow: "hidden",
    },
    soft,
  };
}

export function SectionHeader({ title, hint, icon, A, c }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 14 }}>
      {icon && (
        <span
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: A,
            background: `${A}14`,
            border: `1px solid ${A}28`,
          }}
        >
          {icon}
        </span>
      )}
      <div style={{ minWidth: 0 }}>
        <p style={{ fontSize: 14, fontWeight: 700, color: c.text, margin: 0 }}>{title}</p>
        {hint && <p style={{ fontSize: 12, color: c.sub, margin: "4px 0 0", lineHeight: 1.45 }}>{hint}</p>}
      </div>
    </div>
  );
}

export function SectionCard({ children, theme, bodyPadding = "16px 18px" }) {
  return (
    <div style={theme.section}>
      <div style={{ padding: bodyPadding }}>{children}</div>
    </div>
  );
}

export function FieldLabel({ children, c, mono }) {
  return (
    <label
      style={{
        display: "block",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: c.sub,
        marginBottom: 6,
        fontFamily: mono ? "var(--nm-font-mono)" : "inherit",
      }}
    >
      {children}
    </label>
  );
}

export function Segmented({ options, value, onChange, dark, c, A, fluid, readOnly = false }) {
  return (
    <div
      role="group"
      style={{
        display: "flex",
        gap: 2,
        padding: 3,
        borderRadius: 9,
        width: fluid ? "100%" : undefined,
        background: dark ? "rgba(255,255,255,0.06)" : c.hover,
        border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : c.cardBdr}`,
        opacity: readOnly ? 0.92 : 1,
      }}
    >
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && onChange(opt)}
            style={{
              flex: fluid ? "1 1 0" : undefined,
              minWidth: fluid ? 0 : 32,
              height: 28,
              padding: "0 8px",
              borderRadius: 6,
              border: "none",
              cursor: readOnly ? "default" : "pointer",
              fontSize: 12,
              fontWeight: active ? 700 : 500,
              fontFamily: "inherit",
              fontVariantNumeric: "tabular-nums",
              background: active ? A : "transparent",
              color: active ? "#fff" : c.sub,
              boxShadow: active ? `0 1px 4px ${A}44` : "none",
              transition: "all 0.12s ease",
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export function Toggle({ on, onChange, A, c, readOnly = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={readOnly}
      onClick={() => !readOnly && onChange(!on)}
      style={{
        width: 38,
        height: 22,
        borderRadius: 999,
        border: "none",
        cursor: readOnly ? "default" : "pointer",
        padding: 2,
        background: on ? A : c.cardBdr,
        display: "inline-flex",
        alignItems: "center",
        transition: "background 0.15s ease",
        opacity: readOnly ? 0.85 : 1,
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#fff",
          transform: on ? "translateX(16px)" : "translateX(0)",
          transition: "transform 0.15s ease",
          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
        }}
      />
    </button>
  );
}

function HexChips({ hex, maxBytes, A, dark }) {
  const bytes = hexToBytes(hex, maxBytes);
  if (!bytes.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
      {bytes.map((b, i) => (
        <span
          key={`${b}-${i}`}
          style={{
            fontSize: 10,
            fontWeight: 700,
            fontFamily: "var(--nm-font-mono)",
            padding: "3px 7px",
            borderRadius: 5,
            background: dark ? `${A}20` : `${A}12`,
            color: A,
            border: `1px solid ${A}30`,
          }}
        >
          {b}
        </span>
      ))}
    </div>
  );
}

export function HexField({ label, value, onChange, maxBytes, dark, c, A, theme, placeholder, readOnly = false }) {
  return (
    <div style={{ minWidth: 0 }}>
      <FieldLabel c={c} mono>{label}</FieldLabel>
      <div
        style={{
          borderRadius: 9,
          border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : c.cardBdr}`,
          background: dark ? "rgba(0,0,0,0.25)" : "rgba(15, 23, 42, 0.03)",
          padding: "8px 10px 10px",
        }}
      >
        <input
          type="text"
          value={value}
          readOnly={readOnly}
          onChange={readOnly ? undefined : (e) => onChange(sanitizeHex(e.target.value))}
          placeholder={placeholder}
          style={{ ...theme.input, ...theme.hexInput, height: 30, background: "transparent", border: "none", padding: 0, boxShadow: "none", cursor: readOnly ? "default" : "text" }}
          onFocus={readOnly ? undefined : (e) => { e.currentTarget.parentElement.style.borderColor = A; }}
          onBlur={readOnly ? undefined : (e) => { e.currentTarget.parentElement.style.borderColor = dark ? "rgba(255,255,255,0.1)" : c.cardBdr; }}
        />
        <HexChips hex={value} maxBytes={maxBytes} A={A} dark={dark} />
      </div>
    </div>
  );
}

export function CommandSlotEditor({ index, slot, onChange, dark, c, A, theme, readOnly = false }) {
  const update = (patch) => !readOnly && onChange({ ...slot, ...patch });
  const { input, focus } = theme;

  return (
    <div
      style={{
        borderRadius: 12,
        border: `1px solid ${c.cardBdr}`,
        background: dark ? "rgba(255,255,255,0.02)" : c.hover,
        padding: "14px 16px",
        minWidth: 0,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div aria-hidden style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: A, borderRadius: "12px 0 0 12px" }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 14, paddingLeft: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: A,
              color: "#fff",
              fontSize: 11,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {index + 1}
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>Command {index + 1}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: c.sub }}>Extended</span>
          <Toggle on={slot.extended} onChange={(v) => update({ extended: v })} A={A} c={c} readOnly={readOnly} />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingLeft: 6 }}>
        <HexField
          label="ARB ID"
          value={slot.arbId}
          onChange={(v) => update({ arbId: v })}
          maxBytes={slot.extended ? 4 : 2}
          dark={dark}
          c={c}
          A={A}
          theme={theme}
          placeholder="18DAF110"
          readOnly={readOnly}
        />
        <div>
          <FieldLabel c={c}>DLC</FieldLabel>
          <Segmented options={[0, 1, 2, 3, 4, 5, 6, 7, 8]} value={slot.dlc} onChange={(v) => update({ dlc: v })} dark={dark} c={c} A={A} fluid readOnly={readOnly} />
        </div>
        <HexField
          label="Data payload"
          value={slot.data}
          onChange={(v) => update({ data: v })}
          maxBytes={8}
          dark={dark}
          c={c}
          A={A}
          theme={theme}
          placeholder="00 00 00 00"
          readOnly={readOnly}
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr minmax(88px, 140px)", gap: 12 }}>
          <div>
            <FieldLabel c={c}>Timeout (ms)</FieldLabel>
            <input type="text" inputMode="numeric" value={slot.timeoutMs} readOnly={readOnly} onChange={readOnly ? undefined : (e) => update({ timeoutMs: e.target.value.replace(/\D/g, "") })} style={{ ...input, cursor: readOnly ? "default" : "text" }} {...(readOnly ? {} : focus)} />
          </div>
          <div>
            <FieldLabel c={c}>Count</FieldLabel>
            <input type="text" inputMode="numeric" value={slot.count} readOnly={readOnly} onChange={readOnly ? undefined : (e) => update({ count: e.target.value.replace(/\D/g, "") })} style={{ ...input, cursor: readOnly ? "default" : "text" }} {...(readOnly ? {} : focus)} />
          </div>
        </div>
      </div>
    </div>
  );
}
